"use client";

import { AuthUser, getCurrentUser, fetchAuthSession } from "@aws-amplify/auth";
import { GA_VALUES } from "@packages/constants/ga.const";
import { DataLayerGA4AttrType } from "@packages/lib/types/datalayerGA";
import { ClickstreamInterface } from "@packages/lib/utlils/clickstream";
import {
  clickstreamForClicks,
  isWhatuni,
} from "@packages/lib/utlils/commonFunction";
import {
  GA4DataLayer,
  GA4DataLayerFn,
} from "@packages/lib/utlils/helper-function";
import {
  addRemoveFavourites,
  getUserFavourites,
} from "@packages/lib/utlils/userfavourite";
import {
  ClickstreamConstants,
  GAConstants,
} from "@packages/shared-components/services/constants";
import { useEffect, useRef, useState } from "react";
import { createContext } from "react";
import RegistrationModal from "@packages/shared-components/user-profile/signin-signup/registration-modal";
import emitter, { CustomEvents } from "@packages/lib/eventEmitter/eventEmitter";

type ContentType = "INSTITUTION" | "COURSE";

const PENDING_FAV = "pendingFavourite";

interface Favourite {
  favId: string;
  fav_id?: string;
  favType: string;
  favDate?: string;
  final_choice_id?: string | null;
  choice_position?: number | null;
}

interface FavouriteContext {
  favourites: number[];
  setFavourites?: Function;
  favourite?: (
    contentId: number,
    contentName: string,
    contentType: "INSTITUTION" | "COURSE",
  ) => Promise<{ status: string }>;
}

export const FavouriteContext = createContext<FavouriteContext>({
  favourites: [],
});

const FavFunctionalityWrapper = ({
  children,
  commonGAAttrs,
  cSCommonData,
  commonOldGAAttrs,
}: any) => {
  const [favourites, setFavourites] = useState<number[]>([]);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  // Prevents the pending favourite from being resumed twice when both the
  // modal-close event and PostAuthRefresh fire after sign-in/up (DLG-11779).
  const isResumingRef = useRef(false);

  // Fetch the user's favourites list (used on mount and after PostAuthRefresh)
  const getFavouritesList = async () => {
    try {
      let user: AuthUser | undefined | boolean = undefined;
      if (isWhatuni()) user = await getCurrentUser();
      else {
        // Check Cognito session directly (pgs_userid cookie may not exist yet after OAuth)
        const session = await fetchAuthSession();
        user = !!session?.tokens?.idToken;
        if (!user) throw new Error("Unauthorized user");
      }
      if (user && typeof window !== "undefined") {
        const response = await getUserFavourites();
        const favList: Favourite[] = Array.isArray(response)
          ? response
          : response?.favourites;
        if (favList?.length) {
          let formattedList: number[] = favList?.map(
            (fav: any) => +(fav?.favId || fav?.fav_id),
          );
          setFavourites(() => [...formattedList]);
        }
      }
    } catch (error) {
      setFavourites([]);
    }
  };

  // Listen for PostAuthRefresh to re-fetch favourites (hearts turn blue after modal signin)
  useEffect(() => {
    const handlePostAuth = () => getFavouritesList();
    emitter.on(CustomEvents.PostAuthRefresh, handlePostAuth);
    return () => {
      emitter.off(CustomEvents.PostAuthRefresh, handlePostAuth);
    };
  }, []);

  useEffect(() => {
    async function resumeFavouriteAction() {
      const content = localStorage.getItem(PENDING_FAV);
      if (!content) return;
      // Guard against a double-resume: both the registration-modal-close event
      // and PostAuthRefresh can trigger this. Remove the pending item up front
      // so only the first caller runs it (DLG-11779).
      if (isResumingRef.current) return;
      isResumingRef.current = true;
      localStorage.removeItem(PENDING_FAV);
      const {
        contentId,
        contentName,
        contentType,
        clickStreamData,
        statsGAData,
        statsOldGAData,
      } = { ...JSON.parse(content) };
      // Refresh the favourites list FIRST so the optimistic add in favourite()
      // lands on top of the authoritative list instead of being overwritten by
      // a late-returning getFavouritesList() (the heart stayed empty otherwise).
      await getFavouritesList();
      await favourite(
        contentId,
        contentName,
        contentType,
        true,
        statsGAData,
        clickStreamData,
        statsOldGAData,
      );
      isResumingRef.current = false;
    }

    // Handle modal close event — close modal + resume pending fav
    const handleModalClose = () => {
      setShowRegistrationModal(false);
      resumeFavouriteAction();
    };

    const handlePostAuthResume = () => resumeFavouriteAction();

    getFavouritesList();
    resumeFavouriteAction();

    // Listen for registration-modal-close event (fired after email signin in modal)
    window.addEventListener("registration-modal-close", handleModalClose);
    // Also resume after a social/redirect sign-in that emits PostAuthRefresh.
    emitter.on(CustomEvents.PostAuthRefresh, handlePostAuthResume);
    return () => {
      window.removeEventListener("registration-modal-close", handleModalClose);
      emitter.off(CustomEvents.PostAuthRefresh, handlePostAuthResume);
    };
  }, []);

  const favourite = async (
    contentId: number,
    contentName: string,
    contentType: ContentType,
    isResume?: boolean,
    statsGAData?: any,
    clickStreamData?: any,
    statsOldGAData?: DataLayerGA4AttrType,
  ): Promise<{ status: string }> => {
    let returnMsg: { status: string } = { status: "" };
    // Normalise the id to a number. Some callers (e.g. the open days page,
    // whose uniInfo.institutionId is a string) pass contentId as a string,
    // which made the AddFavoriteRequestList payload send "contentId":"3747"
    // and the favourites API returned 500. The data layer expects a numeric
    // id, so coerce once here so every caller/page sends the same shape
    // (DLG-11779).
    const numericContentId = Number(contentId);
    // Guard against a bad id before doing anything: Number() turns "abc" into
    // NaN and null/"" into 0, none of which are valid institution/course ids.
    // Bail out early so we never send a NaN/0 to the favourites API (which
    // would 500) or favourite the wrong item.
    if (!Number.isInteger(numericContentId) || numericContentId <= 0) {
      console.log("Invalid favourite contentId", contentId);
      return returnMsg;
    }
    try {
      if (isWhatuni()) await getCurrentUser();
      else {
        // Check Cognito session directly (pgs_userid cookie may not exist yet after OAuth)
        const session = await fetchAuthSession();
        if (!session?.tokens?.idToken) throw new Error("Unauthorized user");
      }
    } catch (error) {
      console.log(error, "fav error");
      if (isResume) {
        localStorage.removeItem(PENDING_FAV);
      } else {
        localStorage.setItem(
          PENDING_FAV,
          JSON.stringify({
            contentType,
            contentId: numericContentId,
            contentName,
            clickStreamData,
            statsGAData,
            statsOldGAData,
          }),
        );
        setShowRegistrationModal(true);
      }
      return returnMsg;
    }
    const isAdd = !favourites?.includes(numericContentId);
    const payload = {
      contentType,
      contentId: numericContentId,
      contentName,
      inputFlag: isAdd,
    };
    try {
      updateAction(isAdd, payload);
      const data = await addRemoveFavourites([payload]);
      switch (data?.message?.toLowerCase()) {
        case "added course":
        case "added institution":
          returnMsg.status = "added";
          favGALog(isAdd, statsGAData, statsOldGAData);
          favClickStreamLog(payload.contentType, clickStreamData);
          break;
        case "removed course":
        case "removed institution":
          returnMsg.status = "removed";
          favGALog(isAdd, statsGAData, statsOldGAData);
          break;
        case "limit exceeded":
          returnMsg.status = "limit exceeded";
          updateAction(!isAdd, payload);
          break;
        default:
          updateAction(!isAdd, payload);
      }
    } catch (error) {
      console.log("error", error);
      updateAction(!isAdd, payload);
    }
    return returnMsg;
  };

  function updateAction(isAdd: boolean, payload: any) {
    // Coerce to number so the stored id matches how the heart icon checks
    // membership (favourites?.includes(+contentId)). Storing a raw string id
    // meant the heart never filled after a resumed favourite post sign-in/up
    // (DLG-11779).
    const id = Number(payload?.contentId);
    if (isAdd)
      setFavourites((prev: any[]) => {
        const next = (prev || []).map((ele) => Number(ele));
        return next.includes(id) ? next : [...next, id];
      });
    else
      setFavourites((prev: any[]) => {
        return (prev || [])
          .map((ele) => Number(ele))
          .filter((ele) => ele !== id);
      });
  }

  const favGALog = (
    isAdd: boolean,
    statsGAData?: any,
    statsOldGAData?: DataLayerGA4AttrType,
  ) => {
    GA4DataLayer({
      event_name: isAdd ? GAConstants.favourite : GAConstants.unfavourite,
      event: GAConstants.track_kpi,
      page_name: localStorage?.getItem("gaPageName") || "NA",
      ...statsGAData,
      ...commonGAAttrs,
    });
    statsOldGAData &&
      GA4DataLayerFn({
        eventName: isAdd ? GAConstants.favourite : GAConstants.unfavourite,
        event: "ga_events",
        page_name: localStorage?.getItem("gaPageName") || "NA",
        ...statsOldGAData,
        ...commonOldGAAttrs,
      });
  };

  const favClickStreamLog = (type: ContentType, clickStreamData: any = {}) => {
    let csData: ClickstreamInterface = {} as ClickstreamInterface;
    csData.actionType = ClickstreamConstants.clickAction;
    csData.eventType =
      type === "INSTITUTION"
        ? "InstitutionBasketDetailViewed"
        : "CourseBasketDetailViewed";
    csData.functionalityName = "Favourites";
    const mergedClickStreamData = { ...cSCommonData, ...clickStreamData };
    clickstreamForClicks(mergedClickStreamData, csData, commonGAAttrs);
  };

  return (
    <FavouriteContext.Provider value={{ favourites, setFavourites, favourite }}>
      {children}
      {showRegistrationModal && (
        <RegistrationModal onClose={() => setShowRegistrationModal(false)} />
      )}
    </FavouriteContext.Provider>
  );
};

export default FavFunctionalityWrapper;
